import { Settings } from '@mui/icons-material';
import { Box, FormControlLabel, IconButton, Menu, Switch, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Order } from '../../apiTypes';
import OrderItemStatus from '../../components/OrderItemStatus';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import useMedia from '../../utils/useMedia';
import KitchenCard from './KicthenCard';
import PrepareModal from './PrepareModal';

const KitchenScreen = () => {
  const { kitchens, orders, dishesMap, semiProducts, worker } = useCafe();
  const { lang } = useLocale();
  const { isMobile } = useMedia();

  const [openPrepare, setOpenPrepare] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showPBar, setShowPBar] = useState(true);
  const [openSettings, setOpenSettings] = useState<null | HTMLElement>(null);
  const [selectedKithenId, setSelectedKithenId] = useState(kitchens[0]?.id);
  const [currentTime, setCurrentTime] = useState(new Date().getTime());

  const cards = useMemo(() => {
    const ordered: Record<string, DbDocument<Order>> = {};
    const processing: Record<string, DbDocument<Order>> = {};
    const ready: Record<string, DbDocument<Order>> = {};

    orders
      .filter((order) => !(order.type === 'tableOrder' && order.status === 'ordered'))
      .forEach((order) => {
        Object.values(order.items).forEach((item) => {
          if (!selectedKithenId || dishesMap[item.dishId]?.kitchenId === selectedKithenId) {
            if (item.status === 'ordered') {
              if (ordered[order.id]) {
                const items = ordered[order.id]?.items || {};
                items[item.id] = item;
              } else {
                ordered[order.id] = {
                  ...order,
                  items: { [item.id]: item },
                };
              }
            }

            if (item.status === 'processing') {
              if (processing[order.id]) {
                const items = processing[order.id]?.items || {};
                items[item.id] = item;
              } else {
                processing[order.id] = {
                  ...order,
                  items: { [item.id]: item },
                };
              }
            }

            if (item.status === 'ready') {
              if (ready[order.id]) {
                const items = ready[order.id]?.items || {};
                items[item.id] = item;
              } else {
                ready[order.id] = {
                  ...order,
                  items: { [item.id]: item },
                };
              }
            }
          }
        });
      }, []);

    return {
      ordered,
      processing,
      ready,
    };
  }, [dishesMap, orders, selectedKithenId]);

  const handleChangeKithen = (_: React.SyntheticEvent, id: string) => {
    setSelectedKithenId(id);
  };

  useEffect(() => {
    if (window.localStorage.getItem('showPBar') === 'true') {
      setShowPBar(true);
    } else {
      setShowPBar(false);
    }

    if (window.localStorage.getItem('showInfo') === 'true') {
      setShowInfo(true);
    } else {
      setShowInfo(false);
    }

    const id = setInterval(() => {
      setCurrentTime(new Date().getTime());
    }, 10000);

    return () => {
      clearInterval(id);
    };
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
        <div>
          {kitchens.length > 1 && (
            <Tabs
              value={selectedKithenId}
              onChange={handleChangeKithen}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {kitchens.map((kitchen) => (
                <Tab key={kitchen.id} label={kitchen.name} value={kitchen.id} />
              ))}
            </Tabs>
          )}
        </div>

        <div>
          {semiProducts.length > 0 && (
            <PrimaryButton
              onClick={() => setOpenPrepare(true)}
              disabled={!worker?.credentials.make_semifinished_products}
            >
              {t.make_semifinished_product[lang]}
            </PrimaryButton>
          )}

          <IconButton onClick={(e) => setOpenSettings(e.currentTarget)}>
            <Settings />
          </IconButton>

          {openSettings && (
            <Menu open onClose={() => setOpenSettings(null)} anchorEl={openSettings}>
              <Box display="flex" flexDirection="column" gap={1} p={1} pl={3}>
                <FormControlLabel
                  sx={{ color: (t) => t.palette.text.secondary }}
                  control={
                    <Switch
                      size="small"
                      checked={showPBar}
                      onChange={(e) => {
                        setShowPBar(e.target.checked);
                        window.localStorage.setItem('showPBar', e.target.checked ? 'true' : 'false');
                      }}
                    />
                  }
                  label={t.display_progress_bar[lang]}
                />
                <FormControlLabel
                  sx={{ color: (t) => t.palette.text.secondary }}
                  control={
                    <Switch
                      size="small"
                      checked={showInfo}
                      onChange={(e) => {
                        setShowInfo(e.target.checked);
                        window.localStorage.setItem('showInfo', e.target.checked ? 'true' : 'false');
                      }}
                    />
                  }
                  label={t.display_additional_info[lang]}
                />
              </Box>
            </Menu>
          )}
        </div>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: `${window.innerHeight - (isMobile ? 150 : 178)}px`,
          overflow: 'auto',
          gap: 2,
          p: { xs: 1, md: 3 },
          m: { xs: -1, md: -3 },
        }}
      >
        <Box sx={{ width: 'calc(100% / 3)', minWidth: 300 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
            <OrderItemStatus status="ordered" />
            <Typography variant="h6" textAlign="center">
              {t.status_ordered[lang]}
            </Typography>
          </Box>
          {Object.entries(cards.ordered).map(([key, order]) => (
            <KitchenCard key={key} order={order} showInfo={showInfo} showPBar={showPBar} currentTime={currentTime} />
          ))}
        </Box>

        <Box sx={{ width: 'calc(100% / 3)', minWidth: 300 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
            <OrderItemStatus status="processing" />
            <Typography variant="h6" textAlign="center">
              {t.status_processing[lang]}
            </Typography>
          </Box>
          {Object.entries(cards.processing).map(([key, order]) => (
            <KitchenCard key={key} order={order} showInfo={showInfo} showPBar={showPBar} currentTime={currentTime} />
          ))}
        </Box>

        <Box sx={{ width: 'calc(100% / 3)', minWidth: 300 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
            <OrderItemStatus status="ready" />
            <Typography variant="h6" textAlign="center">
              {t.status_ready[lang]}
            </Typography>
          </Box>
          {Object.entries(cards.ready).map(([key, order]) => (
            <KitchenCard key={key} order={order} showInfo={showInfo} currentTime={currentTime} />
          ))}
        </Box>
      </Box>

      {openPrepare && <PrepareModal onClose={() => setOpenPrepare(false)} />}
    </Box>
  );
};

export default KitchenScreen;
